/**
 * Shop the look drawer — same flow as `component-guide.js` / `CustomGuideCore`.
 * Fetches `?section_id=helper-shop-the-look` (or custom section id) and injects HTML.
 *
 * Product PDP + `custom.looks`: the first kit is rendered in the section response; carousel switches only clone
 * pre-rendered `kit-container.shop-the-look` templates.
 */
class ShopTheLookDialogCore {
  static CACHE = new Map();
  static CACHE_LIMIT = 10;

  constructor() {
    this.popup = document.querySelector('custom-shop-the-look');
    this.loader = this.popup?.querySelector('custom-shop-the-look-loader');
    this.close = this.popup?.querySelector('[data-js-close]');
    this.content = this.popup?.querySelector('custom-shop-the-look-content');
    this.wrapper = this.popup?.querySelector('custom-shop-the-look-wrapper');
    /** Clicked look path (normalized) — carousel shows this slide first when opening from PDP links. */
    this.pendingActiveLookPath = '';

    if (this.popup && this.close) {
      this.popup.addEventListener('click', (e) => {
        if (e.target === this.popup && this.popup.classList.contains('active')) {
          this.hide();
        }
      });
      this.close.addEventListener('click', () => this.hide());
    }
  }

  /**
   * @param {string} pageUrl — pathname (e.g. `/pages/...`); may omit leading `/` when shaped like `pages/...`
   * @param {string} [sectionId] — defaults to `helper-shop-the-look`
   * @param {string} [activeLookPath] — optional look metaobject path so the preview carousel lists this fit first
   *    (from `#trigger:shopTheLook:/products/h||/pages/look`).
   */
  open(pageUrl, sectionId = 'helper-shop-the-look', activeLookPath = '') {
    this.sectionId = sectionId;
    this.pendingActiveLookPath =
      activeLookPath != null && activeLookPath !== '' ? String(activeLookPath).trim() : '';
    let path =
      pageUrl != null && pageUrl !== '' ? String(pageUrl).trim() : '';
    if (path && !path.startsWith('/') && path.includes('/')) {
      path = `/${path}`;
    }
    if (!path) {
      path = window.location.pathname;
    }
    const sep = path.includes('?') ? '&' : '?';
    this.url = `${path}${sep}section_id=${encodeURIComponent(sectionId)}`;
    this.show();
  }

  cacheContent(u, c) {
    ShopTheLookDialogCore.CACHE.set(u, c);
    if (ShopTheLookDialogCore.CACHE.size > ShopTheLookDialogCore.CACHE_LIMIT) {
      ShopTheLookDialogCore.CACHE.delete(ShopTheLookDialogCore.CACHE.keys().next().value);
    }
  }

  getCachedContent(u) {
    return ShopTheLookDialogCore.CACHE.get(u) || null;
  }

  async ensureKitAssets() {
    const root = this.popup;
    if (!root) return;
    if (customElements.get('kit-product')) return;

    const priceSrc = root.dataset.assetPrice;
    const kitSrc = root.dataset.assetKit;
    if (!priceSrc || !kitSrc) return;

    await this._loadScriptOnce(priceSrc);
    await this._loadScriptOnce(kitSrc);
  }

  _loadScriptOnce(src) {
    return new Promise((resolve, reject) => {
      const file = src.split('?')[0].split('/').pop();
      const already = [...document.querySelectorAll('script[src]')].some((el) =>
        (el.getAttribute('src') || '').includes(file)
      );
      if (already) {
        resolve();
        return;
      }
      const s = document.createElement('script');
      s.src = src;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Failed to load ${file}`));
      document.head.appendChild(s);
    });
  }

  async show() {
    await this.ensureKitAssets();

    this.popup.classList.remove('closing');
    this.popup.classList.add('active');
    this.popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open', 'shop-the-look-dialog-active');
    [this.popup, this.loader].forEach((el) => el.classList.add('working'));
    await this.fetchContent(this.url);
  }

  hide() {
    this.pendingActiveLookPath = '';

    this.popup.classList.remove('active');
    this.popup.classList.add('closing');
    this.popup.setAttribute('aria-hidden', 'true');
    this.popup.classList.remove('loaded');
    this.wrapper?.classList.remove('sidebar--opened');
    document.body.classList.remove('shop-the-look-dialog-active');
    if (!document.querySelector('custom-guide.active') && !document.querySelector('product-modal.active')) {
      document.body.classList.remove('modal-open');
    }

    setTimeout(() => {
      this.popup.classList.remove('closing', 'working');
    }, 300);
  }

  async fetchContent(u) {
    let txt = this.getCachedContent(u);
    if (txt) {
      this.renderContent(txt);
      return this._done();
    }
    try {
      const res = await fetch(u);
      txt = await res.text();
      this.cacheContent(u, txt);
      this.renderContent(txt);
    } catch (e) {
      console.error('[ShopTheLookDialog] fetch error:', e);
    }
    this._done();
  }

  renderContent(t) {
    this.content.innerHTML = t;
  }

  setTitle() {
    const title = this.content.querySelector('[data-guide-title]');
    const slot = this.popup.querySelector('.sidebar__header .sidebar__title');
    if (title && slot) {
      slot.textContent = title.textContent.trim();
    }
  }

  /**
   * @param {string} text
   */
  setTitleText(text) {
    const slot = this.popup.querySelector('.sidebar__header .sidebar__title');
    if (slot && text) {
      slot.textContent = text.trim();
    }
  }

  /**
   * Per-slide title decoration: "{prefix} {name}" (e.g. "How to wear it: Travel").
   * Prefix comes from `data-title-prefix` on the dialog root so the string stays translatable.
   * @param {string} name
   */
  setLookTitle(name) {
    const value = name?.trim();
    if (!value) return;
    const prefix = this.popup?.dataset.titlePrefix?.trim();
    this.setTitleText(prefix ? `${prefix} ${value}` : value);
  }

  /**
   * RTE/table tweaks for injected kit HTML (mirrors `_done` on root content).
   * @param {ParentNode} scope
   */
  enhanceInjectedContent(scope) {
    scope.querySelectorAll('.rte a img').forEach((i) => {
      i.parentNode.style.border = 'none';
    });
    scope.querySelectorAll('.rte table').forEach((table) => {
      table.outerHTML = `<div class="table-wrapper">${table.outerHTML}</div>`;
    });
  }

  /**
   * Normalize metaobject paths for matching (`/pages/foo` vs `pages/foo`).
   * @param {string | undefined} p
   */
  static normalizeStlFetchPath(p) {
    if (p == null || p === '') {
      return '';
    }
    let s = String(p).trim();
    if (!s.startsWith('/')) {
      s = `/${s}`;
    }
    const q = s.indexOf('?');
    if (q >= 0) {
      s = s.slice(0, q);
    }
    return s.replace(/\/+$/, '') || s;
  }

  /**
   * Move the slide that matches `pendingActiveLookPath` to the front (clicked look → first carousel image).
   * @param {HTMLElement} track
   * @param {HTMLElement} viewport
   */
  prioritizeClickedLookSlide(track, viewport) {
    const wantRaw = this.pendingActiveLookPath;
    if (!wantRaw || !track) {
      return;
    }
    const want = ShopTheLookDialogCore.normalizeStlFetchPath(wantRaw);
    if (!want) {
      return;
    }

    const slides = [...track.querySelectorAll('[data-stl-slide]')];
    const idx = slides.findIndex((sl) => {
      const got = ShopTheLookDialogCore.normalizeStlFetchPath(sl.dataset.stlFetchPath);
      return got === want || got.endsWith(want) || want.endsWith(got);
    });
    if (idx <= 0) {
      return;
    }

    const picked = slides[idx];
    track.insertBefore(picked, track.firstChild);

    requestAnimationFrame(() => {
      viewport.scrollLeft = 0;
    });

    const t = picked.dataset.stlTitle?.trim();
    if (t) {
      this.setLookTitle(t);
    }
  }

  /**
   * Looks carousel: first kit is already in the section HTML; JS only swaps pre-rendered kit templates.
   */
  initLooksCarousel() {
    const root = this.content.querySelector('[data-stl-looks-carousel]');
    if (!root) {
      return;
    }

    const viewport = root.querySelector('[data-stl-viewport]');
    const track = root.querySelector('[data-stl-track]');
    if (track && viewport && this.pendingActiveLookPath) {
      this.prioritizeClickedLookSlide(track, viewport);
    }
    this.pendingActiveLookPath = '';

    const fitInner = root.querySelector('[data-stl-fit-inner]');
    const errorEl = root.querySelector('[data-stl-fit-error]');
    const slides = [...root.querySelectorAll('[data-stl-slide]')];

    if (!viewport || slides.length === 0 || !fitInner) {
      return;
    }

    /** @type {string | null} */
    let shownPath = fitInner.dataset.stlCurrentPath || null;

    const pathMatchesShown = (fetchPath) => {
      if (!shownPath || !fetchPath) {
        return false;
      }
      return (
        ShopTheLookDialogCore.normalizeStlFetchPath(shownPath) ===
        ShopTheLookDialogCore.normalizeStlFetchPath(fetchPath)
      );
    };

    const getSlides = () => [...root.querySelectorAll('[data-stl-slide]')];

    const getCenteredSlide = () => {
      const slideEls = getSlides();
      if (slideEls.length === 0) {
        return null;
      }
      const vr = viewport.getBoundingClientRect();
      const mid = vr.left + vr.width / 2;
      let best = null;
      let bestDist = Infinity;
      for (const sl of slideEls) {
        const r = sl.getBoundingClientRect();
        const c = r.left + r.width / 2;
        const d = Math.abs(c - mid);
        if (d < bestDist) {
          bestDist = d;
          best = sl;
        }
      }
      return best;
    };

    const debounce = (fn, ms) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), ms);
      };
    };

    const applySlideTitle = (slide) => {
      const t = slide.dataset.stlTitle?.trim();
      if (t) {
        this.setLookTitle(t);
      }
    };

    const findKitTemplate = (fetchPath) => {
      const want = ShopTheLookDialogCore.normalizeStlFetchPath(fetchPath);
      if (!want) {
        return null;
      }
      const bucket = root.querySelector('[data-stl-kit-templates]');
      if (!bucket) {
        return null;
      }
      for (const tpl of bucket.querySelectorAll('template[data-stl-fetch-path]')) {
        const raw = tpl.dataset.stlFetchPath?.trim();
        if (!raw) {
          continue;
        }
        const got = ShopTheLookDialogCore.normalizeStlFetchPath(raw);
        if (got === want || got.endsWith(want) || want.endsWith(got)) {
          return tpl;
        }
      }
      return null;
    };

    const getKitFromTemplate = (tpl) => {
      if (!tpl) {
        return null;
      }
      const fragment = tpl.content.cloneNode(true);
      return fragment.querySelector('kit-container.shop-the-look');
    };

    const renderSlideKit = (slide) => {
      const fetchPath = slide.dataset.stlFetchPath?.trim();
      if (!fetchPath) {
        return;
      }

      if (pathMatchesShown(fetchPath) && fitInner.innerHTML.trim().length > 20) {
        errorEl?.setAttribute('hidden', '');
        applySlideTitle(slide);
        return;
      }

      const kit = getKitFromTemplate(findKitTemplate(fetchPath));
      if (!kit) {
        errorEl?.removeAttribute('hidden');
        return;
      }

      errorEl?.setAttribute('hidden', '');
      kit.querySelectorAll('.shop-the-look__preview').forEach((el) => el.remove());
      fitInner.replaceChildren(kit);
      fitInner.dataset.stlCurrentPath = fetchPath;
      shownPath = fetchPath;
      this.enhanceInjectedContent(fitInner);
      applySlideTitle(slide);
    };

    const prevBtn = root.querySelector('[data-stl-prev]');
    const nextBtn = root.querySelector('[data-stl-next]');

    const updateArrowState = () => {
      if (!prevBtn && !nextBtn) {
        return;
      }
      const slideEls = getSlides();
      if (slideEls.length <= 1) {
        prevBtn?.setAttribute('aria-disabled', 'true');
        nextBtn?.setAttribute('aria-disabled', 'true');
        return;
      }
      const active = getCenteredSlide();
      const idx = active ? slideEls.indexOf(active) : -1;
      const atStart = idx <= 0;
      const atEnd = idx === -1 || idx >= slideEls.length - 1;
      prevBtn?.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      nextBtn?.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    };

    const goToSlide = (slide) => {
      if (!slide) {
        return;
      }
      slide.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'nearest'});
    };

    const step = (dir) => {
      const slideEls = getSlides();
      if (slideEls.length === 0) {
        return;
      }
      const active = getCenteredSlide();
      const idx = active ? slideEls.indexOf(active) : 0;
      const target = slideEls[Math.max(0, Math.min(slideEls.length - 1, idx + dir))];
      if (target && target !== active) {
        goToSlide(target);
      }
    };

    prevBtn?.addEventListener('click', () => step(-1));
    nextBtn?.addEventListener('click', () => step(1));

    const onScrollSettle = debounce(() => {
      const active = getCenteredSlide();
      if (active) {
        renderSlideKit(active);
      }
      updateArrowState();
    }, 120);

    viewport.addEventListener('scroll', onScrollSettle, {passive: true});
    window.addEventListener(
      'resize',
      debounce(() => {
        onScrollSettle();
      }, 200)
    );

    requestAnimationFrame(() => {
      const initial = getSlides()[0] ?? getCenteredSlide();
      if (initial) {
        renderSlideKit(initial);
      }
      updateArrowState();
    });
  }

  _done() {
    [this.popup, this.loader].forEach((el) => el.classList.remove('working'));
    this.sectionId = null;
    requestAnimationFrame(() => {
      this.popup.classList.add('loaded');
      requestAnimationFrame(() => {
        this.wrapper?.classList.add('sidebar--opened');
      });
    });
    this.enhanceInjectedContent(this.content);
    this.setTitle();
    this.initLooksCarousel();
  }
}

if (document.querySelector('custom-shop-the-look')) {
  window.ShopTheLookOpener = new ShopTheLookDialogCore();
  window.dispatchEvent(new Event('shopTheLookDialogReady'));
} else {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('custom-shop-the-look')) {
      window.ShopTheLookOpener = new ShopTheLookDialogCore();
      window.dispatchEvent(new Event('shopTheLookDialogReady'));
    }
  });
}
